import { Alert, Card, SimpleGrid, Skeleton, VStack } from '@chakra-ui/react';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import { formatCurrency } from '@packages/core/utils/currency-formatter';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import { DateRange } from '../../common/reservation-analytic-service';
import SkeletonCard from '../../common/skeleton-card';
import MetricCard from '../../reservation/component/metric-card';
import useOnlineOrderDeliveryOverview from '../logic/use-online-order-delivery-overview';

export default function OnlineOrderDeliveryDetailReportCard(props: { dateRange: DateRange }) {
  const branch = useFocusedBranchContext();
  const { t } = useTranslate();
  const company = useFocusedCompanyContext();

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: props.dateRange.startDate,
    endDate: props.dateRange.endDate,
  });

  useEffect(() => {
    setDateRange({
      startDate: props.dateRange.startDate,
      endDate: props.dateRange.endDate,
    });
  }, [props.dateRange]);

  const { analytics, isLoading } = useOnlineOrderDeliveryOverview(branch.branch?.id ?? '', dateRange);

  if (isLoading) {
    return (
      <VStack gap={4} align="stretch">
        <Skeleton height="40px" />
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </SimpleGrid>
      </VStack>
    );
  }

  if (!analytics) {
    return (
      <Alert.Root status="info" borderRadius="md">
        <BellAlertIcon className="w-4 h-4" />
        <Alert.Title>No data available</Alert.Title>
      </Alert.Root>
    );
  }

  const { current, trends } = analytics;

  return (
    <Card.Root>
      <Card.Header>
        <Card.Title fontWeight={'semibold'} fontSize={'xl'}>
          {t('onlineOrder.report.deliveryDetail', 'Delivery Detail')}
        </Card.Title>
      </Card.Header>
      <Card.Body p={4}>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4} w={'full'}>
          <MetricCard
            title={t('onlineOrder.report.deliveryDetail.totalPickupOrders', 'Total Pickup Orders')}
            value={current.totalPickupOrders}
            trend={trends.totalPickupOrders}
            subtitle={t(
              'onlineOrder.report.deliveryDetail.totalPickupOrders.subtitle',
              'Total pickup orders in the selected period',
            )}
          />
          <MetricCard
            title={t('onlineOrder.report.deliveryDetail.pickupRevenue', 'Pickup Revenue')}
            value={formatCurrency(current.pickupRevenue, company.settings.currency)}
            trend={trends.pickupRevenue}
            subtitle={t('onlineOrder.report.deliveryDetail.pickupRevenue.subtitle', 'Total revenue from pickup orders')}
          />
          <MetricCard
            title={t('onlineOrder.report.deliveryDetail.totalDeliveryOrders', 'Total Delivery Orders')}
            value={current.totalDeliveryOrders}
            trend={trends.totalDeliveryOrders}
            subtitle={t(
              'onlineOrder.report.deliveryDetail.totalDeliveryOrders.subtitle',
              'Total delivery orders in the selected period',
            )}
          />
          <MetricCard
            title={t('onlineOrder.report.deliveryDetail.deliveryRevenue', 'Delivery Revenue')}
            value={formatCurrency(current.deliveryRevenue, company.settings.currency)}
            trend={trends.deliveryRevenue}
            subtitle={t(
              'onlineOrder.report.deliveryDetail.deliveryRevenue.subtitle',
              'Total revenue from delivery orders',
            )}
          />
        </SimpleGrid>
      </Card.Body>
    </Card.Root>
  );
}
